import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { getEvents, getEvent, createEvent, updateEvent, deleteEvent, registerForEvent, unregisterFromEvent } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Calendar, Plus, MapPin, Clock, Users, Edit2, Trash2, CheckCircle2, XCircle, Lock, Eye } from 'lucide-react';
import { toast } from 'sonner';

const Events = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'community',
    location: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    max_participants: '',
    is_private: false
  });

  const eventTypes = [
    { value: 'community', label: 'Community Event', color: 'bg-green-100 text-green-700' },
    { value: 'maintenance', label: 'Maintenance', color: 'bg-orange-100 text-orange-700' },
    { value: 'meeting', label: 'Meeting', color: 'bg-blue-100 text-blue-700' }
  ];

  const fetchEvents = async () => {
    try {
      const data = await getEvents();
      setEvents(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventDetails = async (eventId) => {
    try {
      const data = await getEvent(eventId);
      setSelectedEvent(data);
    } catch (error) {
      toast.error('Failed to load event details');
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      event_type: 'community',
      location: '',
      start_date: '',
      end_date: '',
      start_time: '',
      end_time: '',
      max_participants: '',
      is_private: false
    });
    setEditingEvent(null);
  };

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.start_date || !formData.start_time || !formData.end_time || !formData.location) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      const eventData = {
        ...formData,
        max_participants: formData.max_participants ? parseInt(formData.max_participants) : null
      };

      if (editingEvent) {
        await updateEvent(editingEvent.id, eventData);
        toast.success('Event updated successfully');
      } else {
        await createEvent(eventData);
        toast.success('Event created successfully');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchEvents();
      if (selectedEvent) {
        fetchEventDetails(selectedEvent.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save event');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    try {
      await deleteEvent(eventId);
      toast.success('Event deleted');
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleRegister = async (eventId) => {
    try {
      await registerForEvent(eventId);
      toast.success('Successfully registered!');
      fetchEvents();
      if (selectedEvent) {
        fetchEventDetails(selectedEvent.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register');
    }
  };

  const handleUnregister = async (eventId) => {
    try {
      await unregisterFromEvent(eventId);
      toast.success('Successfully unregistered');
      fetchEvents();
      if (selectedEvent) {
        fetchEventDetails(selectedEvent.id);
      }
    } catch (error) {
      toast.error('Failed to unregister');
    }
  };

  const openEditDialog = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      description: event.description,
      event_type: event.event_type,
      location: event.location,
      start_date: event.start_date,
      end_date: event.end_date || '',
      start_time: event.start_time,
      end_time: event.end_time,
      max_participants: event.max_participants?.toString() || '',
      is_private: event.is_private
    });
    setDialogOpen(true);
  };

  const getEventTypeStyle = (type) => {
    const found = eventTypes.find(t => t.value === type);
    return found?.color || 'bg-slate-100 text-slate-700';
  };

  const isUpcoming = (dateStr, endTime) => {
    const eventDate = new Date(dateStr);
    const today = new Date(new Date().toDateString());
    if (eventDate > today) return true;
    if (eventDate < today) return false;
    // Same day — check end time
    if (endTime) {
      const [hours, minutes] = endTime.split(':').map(Number);
      const now = new Date();
      if (now.getHours() > hours || (now.getHours() === hours && now.getMinutes() >= (minutes || 0))) {
        return false;
      }
    }
    return true;
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>;
  }

  // Event Detail View
  if (selectedEvent) {
    const upcoming = isUpcoming(selectedEvent.start_date, selectedEvent.end_time);
    const spotsLeft = selectedEvent.max_participants 
      ? selectedEvent.max_participants - selectedEvent.registration_count 
      : null;

    return (
      <div className="space-y-6" data-testid="event-detail">
        <Button variant="ghost" onClick={() => setSelectedEvent(null)} className="mb-4">
          ← Back to Events
        </Button>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={getEventTypeStyle(selectedEvent.event_type)}>
                    {eventTypes.find(t => t.value === selectedEvent.event_type)?.label}
                  </Badge>
                  {selectedEvent.is_private && (
                    <Badge className="bg-slate-100 text-slate-700">
                      <Lock className="h-3 w-3 mr-1" /> Private
                    </Badge>
                  )}
                  {!upcoming && <Badge className="bg-slate-700 text-white">Completed</Badge>}
                </div>
                <CardTitle className="text-2xl">{selectedEvent.title}</CardTitle>
              </div>
              {user?.role === 'admin' && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openEditDialog(selectedEvent)}>
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDelete(selectedEvent.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <p className="text-slate-700 whitespace-pre-wrap">{selectedEvent.description}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3 text-slate-600">
                <Calendar className="h-5 w-5" />
                <div>
                  <p className="font-medium">{new Date(selectedEvent.start_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  {selectedEvent.end_date && selectedEvent.end_date !== selectedEvent.start_date && (
                    <p className="text-sm text-slate-500">to {new Date(selectedEvent.end_date).toLocaleDateString()}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 text-slate-600">
                <Clock className="h-5 w-5" />
                <span>{selectedEvent.start_time} - {selectedEvent.end_time}</span>
              </div>
              <div className="flex items-center gap-3 text-slate-600">
                <MapPin className="h-5 w-5" />
                <span>{selectedEvent.location}</span>
              </div>
              <div className="flex items-center gap-3 text-slate-600">
                <Users className="h-5 w-5" />
                <span>
                  {selectedEvent.registration_count} registered
                  {selectedEvent.max_participants && ` / ${selectedEvent.max_participants} max`}
                </span>
              </div>
            </div>

            {/* Registration Section - Only for residents/admins */}
            {user?.role !== 'security' && upcoming && (
              <div className="border-t pt-6">
                {selectedEvent.is_registered ? (
                  <div className="flex items-center justify-between bg-green-50 p-4 rounded-lg">
                    <div className="flex items-center gap-2 text-green-700">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="font-medium">You are registered for this event</span>
                    </div>
                    <Button variant="outline" onClick={() => handleUnregister(selectedEvent.id)}>
                      Cancel Registration
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-slate-50 p-4 rounded-lg">
                    <div>
                      <p className="font-medium">Register for this event</p>
                      {spotsLeft !== null && (
                        <p className="text-sm text-slate-500">{spotsLeft} spots remaining</p>
                      )}
                    </div>
                    <Button 
                      onClick={() => handleRegister(selectedEvent.id)}
                      disabled={spotsLeft !== null && spotsLeft <= 0}
                      data-testid="register-event-btn"
                    >
                      Register Now
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Registered Users - Admin only */}
            {user?.role === 'admin' && selectedEvent.registered_users && selectedEvent.registered_users.length > 0 && (
              <div className="border-t pt-6">
                <h4 className="font-semibold mb-3">Registered Participants ({selectedEvent.registered_users.length})</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {selectedEvent.registered_users.map((u) => (
                    <div key={u.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                      <Users className="h-4 w-4 text-slate-400" />
                      <span>{u.name}</span>
                      {u.unit_number && <span className="text-slate-500">• Unit {u.unit_number}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Security View Notice */}
            {user?.role === 'security' && (
              <div className="bg-blue-50 p-4 rounded-lg flex items-center gap-2 text-blue-700">
                <Eye className="h-5 w-5" />
                <span>View-only access for logistics coordination</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Events List View
  const upcomingEvents = events.filter(e => isUpcoming(e.start_date, e.end_time));
  const pastEvents = events.filter(e => !isUpcoming(e.start_date, e.end_time));

  return (
    <div className="space-y-6" data-testid="events-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Events Calendar</h1>
          <p className="text-sm md:text-base text-slate-600">
            {user?.role === 'security' 
              ? 'View upcoming events for logistics coordination' 
              : 'Discover and register for community events'}
          </p>
        </div>

        {user?.role === 'admin' && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-secondary hover:bg-secondary/90" data-testid="new-event-btn">
                <Plus className="h-4 w-4 mr-2" /> New Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingEvent ? 'Edit Event' : 'Create New Event'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateOrUpdate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Event Title *</Label>
                  <Input
                    id="title"
                    placeholder="Enter event title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    data-testid="event-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="event_type">Event Type</Label>
                  <Select value={formData.event_type} onValueChange={(v) => setFormData({ ...formData, event_type: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {eventTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Event description..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    data-testid="event-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    placeholder="e.g., Clubhouse, Community Hall"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                    data-testid="event-location-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Start Date *</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">End Date</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_time">Start Time *</Label>
                    <Input
                      id="start_time"
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_time">End Time *</Label>
                    <Input
                      id="end_time"
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="max_participants">Max Participants (optional)</Label>
                  <Input
                    id="max_participants"
                    type="number"
                    min="1"
                    placeholder="Leave empty for unlimited"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_private"
                    checked={formData.is_private}
                    onChange={(e) => setFormData({ ...formData, is_private: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="is_private">Private Event (hidden from security personnel)</Label>
                </div>
                <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-event-btn">
                  {submitting ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Your Registered Events */}
      {user?.role !== 'security' && user?.role !== 'admin' && (() => {
        const registeredUpcoming = upcomingEvents.filter(e => e.is_registered);
        if (registeredUpcoming.length === 0) return null;
        return (
          <div data-testid="registered-events-section">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Your Registered Events ({registeredUpcoming.length})</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {registeredUpcoming.map((event) => (
                <Card 
                  key={event.id} 
                  className="border-green-200 bg-green-50/50 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => fetchEventDetails(event.id)}
                  data-testid="registered-event-card"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-green-100 text-green-700 border-green-200">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> Registered
                      </Badge>
                      <Badge className={getEventTypeStyle(event.event_type)}>
                        {eventTypes.find(t => t.value === event.event_type)?.label}
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-slate-900 mb-2">{event.title}</h3>
                    <div className="space-y-1 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        <span>{new Date(event.start_date).toLocaleDateString()}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        <span>{event.start_time} - {event.end_time}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <span>{event.location}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Upcoming Events */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Upcoming Events ({upcomingEvents.length})</h2>
        {upcomingEvents.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="p-8 text-center">
              <Calendar className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No upcoming events</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingEvents.map((event) => (
              <Card 
                key={event.id} 
                className="border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => fetchEventDetails(event.id)}
                data-testid="event-card"
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className={getEventTypeStyle(event.event_type)}>
                      {eventTypes.find(t => t.value === event.event_type)?.label}
                    </Badge>
                    {event.is_private && <Lock className="h-3 w-3 text-slate-400" />}
                    {event.is_registered && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{event.title}</h3>
                  <div className="space-y-1 text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      <span>{new Date(event.start_date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>{event.start_time} - {event.end_time}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      <span>{event.location}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      <span>{event.registration_count} registered{event.max_participants && ` / ${event.max_participants}`}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-500 mb-4">Completed Events ({pastEvents.length})</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pastEvents.map((event) => (
              <Card 
                key={event.id} 
                className="border-slate-200 shadow-sm cursor-pointer opacity-80 hover:opacity-100 transition-opacity"
                onClick={() => fetchEventDetails(event.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="bg-slate-700 text-white">Completed</Badge>
                    <Badge className={getEventTypeStyle(event.event_type)}>
                      {eventTypes.find(t => t.value === event.event_type)?.label}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-slate-700 mb-2">{event.title}</h3>
                  <div className="text-sm text-slate-500">
                    <span>{new Date(event.start_date).toLocaleDateString()}</span>
                    <span className="mx-2">•</span>
                    <span>{event.registration_count} attended</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
